﻿import {UICommonPopup} from "../Tools/Base/UICommonPopup";
import {UINineSliceButton} from "../Tools/Base/UINineSliceButton";
import {UISprite} from "../Tools/Base/UISprite";
import {UIText} from "../Tools/Base/UIText";
import {NUtils} from "../../Utils/NUtils";
import {Core} from "../../Base/Core";
import {RewardType} from "../../enums";
import {WeightedRandomization} from "../../Utils/Utils";
import {UINineSlice} from "../Tools/Base/UINineSlice";
import {gsap} from "gsap";

class AdRewardItem extends UISprite {
    get selected() {
        return !!this.selectedImage.Active;
    }

    set selected(val) {
        this.selectedImage.SetActive(!!val);
    }

    Awake() {
        super.Awake();
        this._rect = new PIXI.Rectangle(0, 0, 1, 1);
        this.reward = null;
        this.selectedImage = this.addChild(new UINineSlice("bg-simple"));
        this.selectedImage.alpha = 0.3;
        this.icon = this.addChild(new UISprite());
        this.label = this.addChild(new UIText());

        this.selected = false;
    }

    Init(reward) {
        this.selected = false;

        this.reward = reward;

        switch (reward.type) {
            case RewardType.MoneyHard:
                this.icon.setImage("ico-diamond");
                this.label.text = reward.valueStr;
                break;

            case RewardType.MoneySoftBySoftSpeed: // Soft money
                let softSpeed = ~~GameManager.gameController.MoneySoftSpeed === 0n ? 1n : GameManager.gameController.MoneySoftSpeed;
                this.icon.setImage("ico-gold");
                this.label.text = NUtils.FormatMoney(softSpeed * BigInt(reward.valueStr));
                break;

            case RewardType.BoosterIncomeX4: // Income x4
                this.icon.setImage("x4");
                // this.label.text = Utils.Format("{0}m", ~~(reward.valueInt / 60));
                this.label.text = NUtils.FormatSecondsToTime(reward.valueInt);
                break;

            case RewardType.Spins: // Spins
                this.icon.setImage("spin-icon");
                this.label.text = reward.valueInt;
                break;

            case RewardType.CreatureFromCollectionWithOffset: // Creature -valueInt
                let bonusCreatureId = Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(reward.valueInt);
                this.icon.setImage(`creatures/${bonusCreatureId}`);
                this.label.text = Core.Localizator.Get("monster");
                break;

            case RewardType.LastShopCreature: // Creature last in shop
                let lastCreatureId = Core.Player.PlanetsSave.CurrentPlanet.GetAvailableForPurchaseCreatures().pop() || 1;
                this.icon.setImage(`creatures/${lastCreatureId}`);
                this.label.text = Core.Localizator.Get("monster");
                break;

            case RewardType.BoosterIncomeX2: // Income x2
                this.icon.setImage("x2");
                // this.label.text = Utils.Format("{0}h", ~~(reward.valueInt / 3600));
                this.label.text = NUtils.FormatSecondsToTime(reward.valueInt);
                break;

            default:
                // Not used or unknown types
                this.icon.setImage("questionmark-ico");
                this.label.text = "";
        }
    }

    fitLayout(rect = this._rect) {
        this._rect.copyFrom(rect);
        this.position.copyFrom(rect);

        rect = rect.clone();
        rect.x = rect.y = 0;
        let size = Math.min(rect.width, rect.height);
        rect.pad((rect.width - size) * -0.5, (rect.height - size) * -0.5);

        let selectedImageRect = rect.clone().pad(-size * 0.05, -rect.height * 0.05);
        this.selectedImage.fitLayout(selectedImageRect);

        let innerRect = selectedImageRect.clone().pad(-size * 0.05);

        let iconRect = innerRect.clone();
        iconRect.height *= 0.8;
        this.icon.fitRect(iconRect);

        let labelRect = innerRect.clone();
        labelRect.height *= 0.2;
        labelRect.y = innerRect.bottom - labelRect.height;
        this.label.fitLayout(labelRect);

    }

    _animation = null;

    _killAnimation() {
        if (this._animation) this._animation.kill();
        this._animation = null;
    }

    blinkRandom(t = 0.2) {
        this._killAnimation();
        this._animation = gsap
            .timeline()
            .set(this, { selected: true }, 0)
            .set(this, { selected: false }, t)
            .eventCallback("onComplete", () => this._killAnimation());
    }

    blinkSelect(t = 0.2, n = 3) {
        this._killAnimation();
        this._animation = gsap.timeline();
        for (let i = 0; i < n; i++) {
            this._animation.set(this, { selected: true }, i * 2 * t * 0.5);
            this._animation.set(this, { selected: false }, (i * 2 + 1) * t * 0.5);
        }
        this._animation.set(this, { selected: true }, n * 2 * t * 0.5);
    }
}

class AdRewards extends UISprite {
    Awake() {
        super.Awake();

        this.rewards = [];

        this.back = this.addChild(new UINineSlice("bg-rounding-28px"));
        this.back.tint = 0x000000;
        this.back.alpha = 0.3;
        this.rewardsContainer = this.addChild(new UISprite());
        this.items = [];

        this.target = null;
        this.spinAnimation = null;
    }

    Init(rewards) {
        this.rewards = [...rewards];

        while (this.items.length < rewards.length) {
            this.items.push(this.rewardsContainer.addChild(new AdRewardItem()));
        }
        while (this.items.length > rewards.length) {
            let item = this.items.shift();
            if (item.parent) item.parent.removeChild(item);
        }

        for (let i = 0; i < this.items.length; i++) {
            this.items[i].Init(rewards[i]);
        }

        if (this.spinAnimation) {
            this.spinAnimation.kill();
            this.spinAnimation = null;
        }
    }

    Spin() {
        if (this.spinAnimation) return;

        this.target = WeightedRandomization.Choose(this.rewards);

        let targetIdx = this.rewards.indexOf(this.target);

        let blinkCount = 15;
        let blinkTime = 0.1;

        let finalBlinks = 3;
        let finalBlinkTime = 0.2;

        // Random sequence
        let chunk = [...this.rewards.keys()];
        let sequence = [];
        // Create sequence not shorter than needed
        while (sequence.length < blinkCount) sequence.push(...chunk);
        // Shuffle the sequence
        sequence.sort((a, b) => Math.random() - 0.5);
        // Cut sequence to needed length
        sequence.splice(blinkCount);
        // Add target item as last item in sequence
        sequence.push(targetIdx);
        // Map indexes to animation callbacks
        sequence = sequence.map((idx) => this.items[idx].blinkRandom.bind(this.items[idx], blinkTime));

        let t = 0;
        this.spinAnimation = gsap.timeline();
        for (let animation of sequence) {
            // Random blinks
            this.spinAnimation.add(animation, t);
            t += blinkTime;
        }

        // Final blinks on target item
        this.spinAnimation.add(
            this.items[targetIdx].blinkSelect.bind(this.items[targetIdx], finalBlinkTime, finalBlinks),
            (blinkCount + 1) * blinkTime
        );

        this.spinAnimation.eventCallback("onComplete", () => {
            console.log("SPIN COMPLETE");
            this.emit("Selected", this.target);
        });
    }

    fitLayout(rect) {
        this.position.copyFrom(rect);
        this.back.width = rect.width;
        this.back.height = rect.height;

        let padding = 15;
        this.rewardsContainer.position.set(padding, padding);

        let itemRect = rect.clone();
        itemRect.x = 0;
        itemRect.y = 0;
        itemRect.width -= padding * 2;
        itemRect.height -= padding * 2;

        // hardcoded 2 rows grid!
        let rows = 2,
            cols = Math.ceil(this.items.length / rows);
        itemRect.width /= cols;
        itemRect.height /= rows;

        for (let i = 0; i < this.items.length; i++) {
            let row = ~~(i / cols),
                col = i % cols;
            itemRect.x = col * itemRect.width * 1;
            itemRect.y = row * itemRect.height * 1.1;
            this.items[i].fitLayout(itemRect);
        }
    }
}

export class UIInterstitialRewardRandom extends UICommonPopup {
    Awake() {
        super.Awake({
            skin: "orange",
        });

        this.titleImage = this.content.addChild(new UISprite("creatures/29")); // Under background

        this.title = this.content.addChild(
            new UINineSliceButton("ribbon2", {
                label: Core.Localizator.Get("interstitial_reward_title"),
            })
        );

        this.spinBtn = this.content.addChild(new UINineSliceButton("btn-green", { label: Core.Localizator.Get("chest_open_spin") }));
        this.claimBtn = this.content.addChild(new UINineSliceButton("btn-green", { label: Core.Localizator.Get("collect") }));
        this.rewards = this.content.addChild(new AdRewards());

        this.chosenReward = null; // InterstitialReward

        this.spinBtn.on("pointertap", () => {
            this.spinBtn.interactive = false;
            this.spinBtn.setImage("btn-gray");
            this.rewards.Spin();
        });

        this.claimBtn.on("pointertap", () => {
            if (this.chosenReward) {
                this.chosenReward.Take("interstitial_bonus");
                this.Hide();
            }
        });

        this.rewards.on("Selected", (reward) => {
            console.log("SPIN RESULT", reward);
            this.chosenReward = reward;
            this.spinBtn.SetActive(false);
            this.claimBtn.SetActive(true);
        });
    }

    Show() {
        this.chosenReward = null;

        this.spinBtn.SetActive(true);
        this.spinBtn.interactive = true;
        this.spinBtn.setImage("btn-green");

        this.claimBtn.SetActive(false);

        this.rewards.Init(
            Core.Boosters.IsIncomeActive
                ? Core.GameData.InterstitialRewards.rewardsX2Active
                : Core.GameData.InterstitialRewards.rewardsX2Unactive
        );

        this.fitLayout();

        super.Show();
    }

    fitLayout(viewport = Core.app.viewport) {
        let sideRatio = 1.6;
        let w = Math.min(viewport.width * 0.8, (viewport.height * 0.8) / sideRatio);
        let h = w * sideRatio;

        this.setPopupSize(w, h);

        super.fitLayout(viewport);

        let contentRect = this.getContentSize();

        let rect = contentRect.clone();
        rect.width = contentRect.width * 0.7;
        rect.height = contentRect.width * 0.7;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 0;
        this.titleImage.fitRect(rect);

        rect = contentRect.clone();
        rect.width = contentRect.width * 1.2;
        rect.height = contentRect.height * 0.2;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 0.35;
        this.title.fitLayout(rect);

        rect = new PIXI.Rectangle(0, 0, this.title.width, this.title.height).pad(-this.title.width * 0.15, -this.title.height * 0.15);
        this.title.content.fitLayout(rect);

        rect = this.getPopupRect().clone();
        rect.height *= 0.65;
        rect.y += contentRect.height - rect.height;
        this.back.fitLayout(rect);

        rect = contentRect.clone();
        rect.height = contentRect.height * 0.65;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 1;

        contentRect = rect.clone();

        rect = contentRect.clone();
        rect.width = contentRect.width * 0.8;
        rect.height = contentRect.height * 0.55;
        rect.x = contentRect.x + (contentRect.width - rect.width) * 0.5;
        rect.y = contentRect.y + (contentRect.height - rect.height) * 0.5;
        this.rewards.fitLayout(rect);

        rect = contentRect.clone();
        rect.width = contentRect.width * 0.35;
        rect.height = contentRect.height * 0.15;
        rect.x = contentRect.x + (contentRect.width - rect.width) * 0.5;
        rect.y = contentRect.y + (contentRect.height - rect.height) * 0.95;
        this.spinBtn.fitLayout(rect);
        this.claimBtn.fitLayout(rect);
    }
}
